import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Dialog,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Badge,
  Fab,
  Avatar,
  Tooltip,
  Zoom,
  Slide,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import {
  QrCodeScanner,
  Build,
  Assignment,
  Schedule,
  Warning,
  PriorityHigh,
  Visibility,
  TrendingUp,
  Speed,
  AutoAwesome,
  Bolt,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import usersService, { User } from '../../services/usersService';
import PreparationForm from '../forms/PreparationForm';
import WorkflowQRScanner from '../WorkflowQRScanner';
import ProgressiveWorkflowDisplay from '../ProgressiveWorkflowDisplay';

const TechnicianDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPreparationForm, setShowPreparationForm] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [completedAction, setCompletedAction] = useState<string | null>(null);
  const [stats, setStats] = useState({
    ready_for_prep: 0,
    in_preparation: 0,
    urgent_samples: 0,
  });

  useEffect(() => {
    loadPreparationData();
  }, []);

  const loadPreparationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load samples and technicians in parallel
      // Include both RECEPTION and PREPARATION_MECANIQUE samples for technicians
      const [allSamplesResponse, techniciansData] = await Promise.all([
        samplesService.getSamples(1, 100, {
          status: [SampleStatus.RECEPTION, SampleStatus.PREPARATION_MECANIQUE],
        }),
        usersService.getTechnicians()
      ]);
      
      // Separate samples by status
      const readySamples = allSamplesResponse.samples.filter(s => s.statut === SampleStatus.RECEPTION);
      const inPrepSamples = allSamplesResponse.samples.filter(s => s.statut === SampleStatus.PREPARATION_MECANIQUE);
      
      // Show all samples that technicians can work with
      setSamples(allSamplesResponse.samples);
      setTechnicians(techniciansData);
      
      // Calculate stats from real data
      const urgentSamples = allSamplesResponse.samples.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      setStats({
        ready_for_prep: readySamples.length,
        in_preparation: inPrepSamples.length,
        urgent_samples: urgentSamples,
      });
    } catch (error) {
      console.error('Error loading preparation data:', error);
      setError('Erreur lors du chargement des données de préparation');
    } finally {
      setLoading(false);
    }
  };

  const handleQRScanSuccess = async (sample: Sample) => {
    try {
      setSelectedSample(sample);
      setShowQRScanner(false);
      setShowPreparationForm(true);
    } catch (error) {
      console.error('Error loading sample:', error);
      setError('Échantillon non trouvé ou erreur de chargement');
    }
  };

  const handlePreparationCompleted = async (data: any, action: 'SAVE_DRAFT' | 'START_PREPARATION' | 'COMPLETE_PREPARATION') => {
    try {
      
      if (!selectedSample) {
        console.error('No selected sample for preparation completion');
        setError('Aucun échantillon sélectionné');
        return;
      }

      let newStatus = selectedSample.statut;
      let statusMessage = '';
      
      // Determine status based on action
      switch (action) {
        case 'SAVE_DRAFT':
          // Keep current status, just save data
          statusMessage = 'Brouillon de préparation sauvegardé';
          break;
        case 'START_PREPARATION':
          newStatus = SampleStatus.PREPARATION_MECANIQUE;
          statusMessage = 'Préparation mécanique commencée';
          break;
        case 'COMPLETE_PREPARATION':
          // 🔬 BGM METHOD: Check if sample has multi-lab routing
          if (selectedSample.metadonnees?.bgm_method_selected === true) {
            // For BGM samples, route to the first laboratory (ICP for chemical prep)
            // The sample will appear in both ICP and AU dashboards due to metadata
            newStatus = SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE;
            statusMessage = 'Préparation mécanique terminée - Échantillon BGM routé vers ICP et AU';
          } else {
            // For regular samples, check the first involved laboratory
            const firstLab = selectedSample.laboratories_involved?.[0];
            if (firstLab === 'LABORATOIRE_ICP') {
              newStatus = SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE;
            } else if (firstLab === 'LABORATOIRE_SAA') {
              newStatus = SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE;
            } else if (firstLab === 'LABORATOIRE_AU') {
              newStatus = SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE;
            } else if (firstLab === 'LABORATOIRE_VOLUMETRIE') {
              newStatus = SampleStatus.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE;
            } else {
              // Fallback to legacy status
              newStatus = SampleStatus.ANALYSE_CHIMIQUE;
            }
            statusMessage = 'Préparation mécanique terminée - Prêt pour analyse';
          }
          break;
      }
      
      
      // Update sample status if changed
      if (newStatus !== selectedSample.statut) {
        await samplesService.updateSampleStatus(
          selectedSample.id, 
          newStatus,
          statusMessage
        );
      }
      
      // Always update sample with preparation data
      const updateData = {
        date_fin_preparation: action === 'COMPLETE_PREPARATION' ? new Date().toISOString() : undefined,
        metadonnees: {
          ...selectedSample.metadonnees,
          preparation_data: data,
          preparation_status: action,
          last_updated: new Date().toISOString()
        }
      };
      
      await samplesService.updateSample(selectedSample.id, updateData);
      
      // Close form and refresh data
      setShowPreparationForm(false);
      setSelectedSample(null);
      
      await loadPreparationData();
      
      setSuccessMessage(statusMessage);
      setCompletedAction(action);
      
    } catch (error) {
      console.error('Error handling preparation:', error);
      setError(`Erreur lors de la sauvegarde de la préparation: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      
      // Don't close the form on error so user can retry
      // setShowPreparationForm(false);
      // setSelectedSample(null);
    }
  };

  const handleSampleClick = (sample: Sample) => {
    setSelectedSample(sample);
    setShowPreparationForm(true);
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      case Priority.NORMALE:
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return <PriorityHigh />;
      case Priority.ELEVEE:
        return <Warning />;
      default:
        return <Schedule />;
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Chargement des données...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      minHeight: '100vh',
    }}>
      {/* Modern Header with Glassmorphism */}
      <Fade in timeout={800}>
        <Card sx={{ 
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          borderRadius: 4,
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: '16px 16px 0 0',
          }
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Bonjour, {user?.firstName} 👋
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                  Tableau de bord - Préparation mécanique des échantillons
                </Typography>
              </Box>
              <Avatar 
                sx={{ 
                  width: 80, 
                  height: 80,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  fontSize: '2rem',
                  fontWeight: 'bold',
                }}
              >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {error && (
        <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Slide>
      )}

      {/* Modern Stats Cards with Animations */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            icon: Assignment,
            value: stats.ready_for_prep,
            label: 'Prêts pour préparation',
            color: theme.palette.primary.main,
            gradient: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            delay: 200,
          },
          {
            icon: Build,
            value: stats.in_preparation,
            label: 'En préparation',
            color: theme.palette.warning.main,
            gradient: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
            delay: 400,
          },
          {
            icon: PriorityHigh,
            value: stats.urgent_samples,
            label: 'Échantillons urgents',
            color: theme.palette.error.main,
            gradient: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            delay: 600,
          },
        ].map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Zoom in timeout={stat.delay}>
              <Card sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 4,
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(stat.color, 0.3)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(5deg)',
                  },
                  '& .stat-progress': {
                    width: '100%',
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: stat.gradient,
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        variant="h3" 
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          color: stat.color,
                          mb: 1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography 
                        color="text.secondary" 
                        variant="body1"
                        sx={{ fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                    <Box
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 3,
                        background: stat.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <stat.icon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Box
                    className="stat-progress"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      height: '3px',
                      width: '60%',
                      background: stat.gradient,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Enhanced Main Action Button */}
      <Fade in timeout={1000}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<QrCodeScanner />}
            onClick={() => setShowQRScanner(true)}
            sx={{ 
              py: 3, 
              px: 6, 
              fontSize: '1.2rem',
              fontWeight: 600,
              borderRadius: 4,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                '&::before': {
                  transform: 'translateX(100%)',
                }
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.2)}, transparent)`,
                transition: 'transform 0.6s',
              }
            }}
          >
            <AutoAwesome sx={{ mr: 1 }} />
            Scanner QR Code
            <Bolt sx={{ ml: 1 }} />
          </Button>
        </Box>
      </Fade>

      {/* Enhanced Instructions */}
      <Fade in timeout={1200}>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.1)})`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            '& .MuiAlert-icon': {
              color: theme.palette.info.main,
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            <strong>Instructions:</strong> Scannez le QR code de l'échantillon pour ouvrir automatiquement 
            le formulaire de préparation mécanique. L'emplacement et le statut seront mis à jour automatiquement.
          </Typography>
        </Alert>
      </Fade>

      {/* Modern Samples List */}
      <Fade in timeout={1400}>
        <Paper sx={{ 
          borderRadius: 4,
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          overflow: 'hidden',
        }}>
          <Box sx={{ 
            p: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <TrendingUp color="primary" />
              Échantillons en Attente de Préparation
              <Chip 
                label={samples.length} 
                color="primary" 
                size="small"
                sx={{ 
                  fontWeight: 600,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              />
            </Typography>
          </Box>
          
          {samples.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Speed sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                Aucun échantillon en attente de préparation mécanique
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Les nouveaux échantillons apparaîtront ici automatiquement
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {samples.map((sample, index) => (
                <React.Fragment key={sample.id}>
                  <Zoom in timeout={1600 + index * 100}>
                    <ListItem
                      sx={{ 
                        px: 3,
                        py: 2,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': { 
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                          transform: 'translateX(8px)',
                          '& .sample-actions': {
                            opacity: 1,
                            transform: 'translateX(0)',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: sample.priorite === Priority.URGENTE 
                            ? `linear-gradient(180deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                            : sample.priorite === Priority.ELEVEE
                            ? `linear-gradient(180deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                            : `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover::before': {
                          opacity: 1,
                        }
                      }}
                      onClick={() => handleSampleClick(sample)}
                    >
                      <ListItemIcon>
                        <Badge
                          color={getPriorityColor(sample.priorite as Priority)}
                          variant="dot"
                          invisible={sample.priorite === Priority.NORMALE}
                        >
                          <Avatar sx={{ 
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            width: 48,
                            height: 48,
                          }}>
                            <Assignment />
                          </Avatar>
                        </Badge>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6" fontWeight="600">
                              {sample.sample_id}
                            </Typography>
                            <Chip
                              icon={getPriorityIcon(sample.priorite as Priority)}
                              label={sample.priorite}
                              size="small"
                              color={getPriorityColor(sample.priorite as Priority)}
                              sx={{ fontWeight: 500 }}
                            />
                            <Chip
                              label={sample.type_echantillon}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              <strong>Client:</strong> {sample.client?.company_name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              <strong>Localisation:</strong> {sample.localisation_stockage || 'Zone de réception'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Reçu:</strong> {sample.date_reception ? new Date(sample.date_reception).toLocaleDateString('fr-FR') : 'N/A'}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <Box 
                        className="sample-actions"
                        display="flex" 
                        gap={1}
                        sx={{
                          opacity: 0,
                          transform: 'translateX(20px)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <Tooltip title="Voir détails" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/samples/${sample.id}`);
                            }}
                            sx={{
                              background: alpha(theme.palette.info.main, 0.1),
                              '&:hover': {
                                background: alpha(theme.palette.info.main, 0.2),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <Visibility color="info" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Commencer préparation" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSampleClick(sample);
                            }}
                            sx={{
                              background: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                background: alpha(theme.palette.primary.main, 0.2),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <Build color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  </Zoom>
                  {index < samples.length - 1 && (
                    <Divider sx={{ 
                      mx: 3,
                      background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.5)}, transparent)`,
                    }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Fade>

      {/* Enhanced QR Scanner FAB */}
      <Zoom in timeout={2000}>
        <Fab
          color="primary"
          aria-label="scan qr"
          sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24,
            width: 64,
            height: 64,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.1) rotate(5deg)',
              boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
            }
          }}
          onClick={() => setShowQRScanner(true)}
        >
          <QrCodeScanner sx={{ fontSize: 28 }} />
        </Fab>
      </Zoom>

      {/* QR Scanner Dialog */}
      {showQRScanner && (
        <WorkflowQRScanner
          mode="dialog"
          onClose={() => setShowQRScanner(false)}
          onSampleProcessed={handleQRScanSuccess}
        />
      )}

      {/* Preparation Form Dialog */}
      <Dialog
        open={showPreparationForm}
        onClose={() => setShowPreparationForm(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        {selectedSample && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Left Panel - Progressive Workflow Display */}
            <Box sx={{ 
              width: '40%', 
              p: 3, 
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`, 
              overflow: 'auto',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
            }}>
              <Typography variant="h5" sx={{ 
                mb: 3, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                fontWeight: 600,
              }}>
                <Assignment color="primary" />
                Échantillon {selectedSample.sample_id}
              </Typography>
              <ProgressiveWorkflowDisplay
                sample={selectedSample}
                showEditButtons={false}
              />
            </Box>
            
            {/* Right Panel - Preparation Form */}
            <Box sx={{ width: '60%', overflow: 'auto' }}>
              <PreparationForm
                initialData={{
                  sample_id: selectedSample.sample_id,
                  ...(selectedSample.metadonnees?.preparation_data || {})
                }}
                onSubmit={handlePreparationCompleted}
                onCancel={() => setShowPreparationForm(false)}
                technicians={technicians.map(tech => ({
                  id: tech.id,
                  name: `${tech.firstName} ${tech.lastName}`,
                  speciality: tech.department
                }))}
                equipment={[
                  // Concassage Equipment
                  { id: 'concasseur-machoires-01', name: 'Concasseur à Mâchoires CM-01', type: 'CONCASSAGE', status: 'AVAILABLE' },
                  { id: 'concasseur-giratoire-01', name: 'Concasseur Giratoire CG-01', type: 'CONCASSAGE', status: 'AVAILABLE' },
                  { id: 'concasseur-cone-01', name: 'Concasseur à Cône CC-01', type: 'CONCASSAGE', status: 'AVAILABLE' },
                  { id: 'broyeur-marteaux-01', name: 'Broyeur à Marteaux BM-01', type: 'CONCASSAGE', status: 'AVAILABLE' },
                  
                  // Broyage Equipment
                  { id: 'broyeur-boulets-01', name: 'Broyeur à Boulets BB-01', type: 'BROYAGE', status: 'AVAILABLE' },
                  { id: 'broyeur-barres-01', name: 'Broyeur à Barres BR-01', type: 'BROYAGE', status: 'AVAILABLE' },
                  { id: 'broyeur-disque-01', name: 'Broyeur à Disque BD-01', type: 'BROYAGE', status: 'AVAILABLE' },
                  { id: 'pulverisateur-01', name: 'Pulvérisateur PV-01', type: 'BROYAGE', status: 'AVAILABLE' },
                  { id: 'mortier-agate-01', name: 'Mortier en Agate MA-01', type: 'BROYAGE', status: 'AVAILABLE' },
                  { id: 'broyeur-planetaire-01', name: 'Broyeur Planétaire BP-01', type: 'BROYAGE', status: 'AVAILABLE' },
                  
                  // Auxiliary Equipment
                  { id: 'balance-precision-01', name: 'Balance de Précision BP-01', type: 'PESAGE', status: 'AVAILABLE' },
                  { id: 'etuve-sechage-01', name: 'Étuve de Séchage ES-01', type: 'SECHAGE', status: 'AVAILABLE' },
                  { id: 'diviseur-echantillon-01', name: 'Diviseur d\'Échantillon DE-01', type: 'DIVISION', status: 'AVAILABLE' },
                  { id: 'tamis-vibrant-01', name: 'Tamis Vibrant TV-01', type: 'TAMISAGE', status: 'AVAILABLE' }
                ]}
              />
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Success Notification */}
      {successMessage && (
        <Slide direction="up" in={!!successMessage} mountOnEnter unmountOnExit>
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }} 
            onClose={() => {
              setSuccessMessage(null);
              setCompletedAction(null);
            }}
          >
            {successMessage}
          </Alert>
        </Slide>
      )}
    </Box>
  );
};

export default TechnicianDashboard; 